import { useState } from 'react';
import useAuth from "./auth";
import useContacts from "./contacts";
import AppService, { Utilities } from "s2s-app-service";
import { format, isToday, isYesterday } from 'date-fns';
import { extractContactNameFromContactArray, processServiceErrorsToAlerts } from '../js/utilities';
import useAlerts from '../hooks/alerts';

import {
    LS_SECTION_KEYS,
    storeLocalData,
    retrieveLocalData,
} from './../js/localData';

const LS_SECTION_KEY = LS_SECTION_KEYS.CALL_HISTORY;

const useCallHistory = () => {
  const [callHistory, setCallHistory] = useState([]);
  const [, , , { getContactDataByPhoneNumber }] = useContacts();
  const [auth] = useAuth();
  const [, { createErrorAlert }] = useAlerts();


  const getCallHistory = async () => {
    try {
      if (
        auth.isOnline === false || 
        typeof auth.auth === "undefined" ||
        typeof auth.auth.telephony_data === "undefined" ||
        typeof auth.auth.telephony_data.xmpp === "undefined"
      ) {
        const cachedHistory = retrieveLocalData(LS_SECTION_KEY, { callHistoryCached: [] });
        console.warn(`attempting to use cached call history. online: ${auth.isOnline}`, auth);
        setCallHistory(typeof cachedHistory.callHistoryCached !== "undefined" ? cachedHistory.callHistoryCached : {});
        return {};
      } else {
        const userCallHistory = await AppService.getInstance().speak("getUserActivity", { "user_id": auth.auth.telephony_data.xmpp.userId, "limit": 100, "offset": 0 });

        let contactPromises = [];

        let callNumberCache = [];
        let formattedHistory = userCallHistory.map((historyItem) => {
          // parse bean for JSON object
          let callItem = JSON.parse(historyItem.activityBean);

          let callNumber = callItem.direction === 'inbound' ? callItem.sourceid : callItem.destinationid;
          // Need to remove leading '+1'.  This will have to be modified as new
          // international number formats are utilized
          callNumber = callNumber.length === 11 ? callNumber.slice(1) : callNumber;

          // only look up contact name if valid 10 digit number and if we haven't already looked it up
          if (callNumber.length === 10 && !callNumberCache.includes(callNumber)) {
            callNumberCache.push(callNumber);
            contactPromises.push(getContactDataByPhoneNumber(callNumber));
          }
          //we are giving each item a random key attribute that is used in the rendering of the list
          //we can't create a key with out using a random number becuase the API doubles up on listing certain calls
          return {
            callInfo: callItem,
            contactName: callNumber, // until names are available
            endTime: historyItem.endTime,
            dateInfo: new Date(historyItem.endTime),
            activityType: historyItem.activityType,
            key: Math.random()
          };
        });

        // await for all contact promises to resolve
        let contactObjects = await Promise.all(contactPromises);

        // concatenate the contactObject items into a single array of contacts
        let contactArray = [];
        contactObjects.forEach((obj) => {
          contactArray = contactArray.concat(obj.items);
        });

        const CategorizedCallHistory = formattedHistory.length > 0 ? formattedHistory.reduce((historyObj, historyItem) => {
          const itemFormatedDate = format(historyItem.endTime, 'MMMM DD, YYYY');

          // Add the contactName to the historyItem from the contactArray that we created
          let callNumber = historyItem.callInfo.direction === 'inbound' ? historyItem.callInfo.sourceid : historyItem.callInfo.destinationid;
          historyItem.contactName = extractContactNameFromContactArray(callNumber, contactArray);

          if (isToday(itemFormatedDate)) {
            historyObj["Today"].push(historyItem);
          } else if (isYesterday(itemFormatedDate)) {
            historyObj["Yesterday"].push(historyItem);
          } else {
            if (typeof historyObj[itemFormatedDate] !== "undefined") {
              historyObj[itemFormatedDate].push(historyItem);
            } else {
              historyObj[itemFormatedDate] = [historyItem];
            }
          }
          return historyObj;
        }, { "Today": [], "Yesterday": [] }) : [];

        storeLocalData(LS_SECTION_KEY, { callHistoryCached: CategorizedCallHistory });
        setCallHistory(CategorizedCallHistory);
      }
    } catch (e) {
      console.error('Error getting call history', e);
      const { alertHeading:message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

      createErrorAlert({
        message,
        details: [alertBody],
        autoCloseTimeout: 10,
      });
    }
  };
  
  return [callHistory, { getCallHistory }];
};

//exporting
export default useCallHistory;

